import Ember from 'ember';
import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
// @ts-ignore
import ActionCell from 'volta/components/cell/action-cell';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
// @ts-ignore
import PlanningPriorityCell from 'volta/components/cell/planning-priority-cell';
import TaskCell from 'volta/components/cell/task-cell';
import CollectionView from 'volta/models/collection-view';
import { ResourceName as ExecutionResourceName } from 'volta/models/execution-alert';
import PlanningModel from 'volta/models/planning';
import SkuModel, { SkuResourceName } from 'volta/models/sku';
import { TaskListChangeEvents } from 'volta/models/task';
import POGroupsCollectionDefinitionBuilder from 'volta/services/collection-builder-po-groups';
import SessionUserService from 'volta/services/session-user';
import { SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { collectionViewToQueryParams, eqFilter } from 'volta/utils/filters-utils';
import { classNames, getBufferZoneCriticality } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { IBvTableColumn, IBvTableSort } from 'volta/components/bv-table';
import type ExecutionAlertModel from 'volta/models/execution-alert';
import type TaskService from 'volta/services/task-service';

import type IntlService from 'ember-intl/services/intl';

import type {
  IExecutionAlertTs,
  IExecutionProjectionTsItem,
  IProjectedExecutionOrder
} from 'volta/models/types/planning';
import type AppSettings from 'volta/services/app-settings';
import type BvMetricsService from 'volta/services/bv-metrics';
import type EventStreamService from 'volta/services/event-stream';
import type ExecutionAlertsTsData from 'volta/services/execution-alerts-ts-data';
import type { TSkuDetailModalTab } from 'volta/services/sku-modal-service';
import type StoreService from 'volta/services/store';
import type { IDashboardItem } from 'volta/models/types/app-features';
import type LocalizationService from 'volta/services/localization';
import type { IPagesPreferences } from 'volta/models/types/user';
import type { ISkuPagePrefs } from '..';
import type SkuTask from 'volta/models/sku-task';

interface IProjectedExecutionItemEnriched extends IExecutionProjectionTsItem {
  key: number;
  tasks: SkuTask[];
}

interface IExecutionDashboardItem extends IDashboardItem {
  onClick?: (self: SkuDetailsExecution) => void;
}

const DASHBOARD: Record<string, Partial<IExecutionDashboardItem>> = {
  onHand: {
    onClick: (self) => self && self.args.switchTab?.('stock')
  }
};

interface IArgs {
  skuId: string;
  sku: SkuModel;
  switchTab?: (tab: TSkuDetailModalTab) => void;
  showOpenOrders?: (peo: IProjectedExecutionItemEnriched) => void;
  inModal?: boolean;
}

export default class SkuDetailsExecution extends Component<IArgs> {
  // Services
  // ~~~~

  @service store!: StoreService;
  @service intl!: IntlService;
  @service localization!: LocalizationService;
  @service eventStream!: EventStreamService;
  @service executionAlertsTsData!: ExecutionAlertsTsData;
  @service bvMetrics!: BvMetricsService;
  @service appSettings!: AppSettings;
  @service taskService!: TaskService;
  @service sessionUser!: SessionUserService;
  @service router!: RouterService;
  @service collectionBuilderPoGroups!: POGroupsCollectionDefinitionBuilder;

  // Properties
  // ~~~~

  @tracked emptyRowsVisible = false;
  @tracked executionAlert!: ExecutionAlertModel;
  @tracked selection = [];
  @tracked projection?: IProjectedExecutionOrder;
  @tracked executionOrdersSorts: IBvTableSort[] = [{ valuePath: 'dueDate', isAscending: true }];
  @tracked chartData?: IExecutionAlertTs;
  @tracked notCalculated: boolean = false;
  @tracked scrollToDate?: string = undefined;
  @tracked executionTasks: SkuTask[] = [];
  resourceName = ExecutionResourceName;
  skuResourceName = SkuResourceName;
  collectionDefinition = this.collectionBuilderPoGroups.build({});

  // Computed Properties
  // ~~~~
  //

  get dashboardConfig() {
    return (this.appSettings.features?.execution.dashboard ?? []).map((item) => {
      return {
        ...item,
        ...(DASHBOARD[item.key as keyof typeof DASHBOARD] ?? {})
      };
    });
  }

  get projectionAsArray() {
    if (!this.projection) {
      return [];
    } else {
      const projectionArr: IProjectedExecutionItemEnriched[] = [];
      const { dueDate } = this.projection;
      const cols = Object.keys(this.projection);
      if (!isEmpty(dueDate)) {
        dueDate.forEach((_date: string, index: number) => {
          const obj: any = { key: index };
          cols.forEach((col) => {
            obj[col as keyof IProjectedExecutionItemEnriched] =
              this.projection?.[col as keyof Omit<IExecutionProjectionTsItem, 'key'>]?.[index];
          });
          projectionArr.push(obj);
        });
      }
      return projectionArr;
    }
  }

  /**
   * DO NOT REMOVE, it's used by the sku-details execution dashboard
   */
  get projectionEndDate() {
    const { dueDate } = this.projection ?? {};
    if (!dueDate || !dueDate.length) {
      return undefined;
    }
    return dueDate.slice().pop();
  }

  get horizon() {
    return this.projection?.dueDate.length;
  }

  get filteredExecutionOrders() {
    const emptyRowsVisible = this.emptyRowsVisible;
    return this.projectionAsArray.filter((item) => {
      return !(
        !emptyRowsVisible &&
        item.demandQty === 0 &&
        item.supplyQty === 0 &&
        item.simulatedDemandQty === 0
      );
    });
  }

  get hiddenProjectionCount() {
    return (this.horizon ?? 0) - (this.filteredExecutionOrders.length ?? 0);
  }

  @sort('filteredExecutionOrders', 'executionOrdersSortProperties')
  sortedExecutionOrders!: IProjectedExecutionItemEnriched[];

  get executionOrdersSortProperties() {
    return this.executionOrdersSorts.flatMap((sort) => {
      const asc = `${sort.isAscending ? 'asc' : 'desc'}`;
      if (sort.sortKey === 'priority') {
        return [`realBufferZone:${asc}`, `realBufferPenetration:${asc}`];
      } else if (sort.sortKey === 'simulatedPriority') {
        return [`simulatedBufferZone:${asc}`, `simulatedBufferPenetration:${asc}`];
      } else {
        return [`${sort.valuePath}:${asc}`];
      }
    });
  }

  get bufferZoneClassName() {
    return this.executionAlert && `buffer-zone-${this.executionAlert.priorityName}`;
  }

  get defaultProjectionColumns(): Record<string, IBvTableColumn> {
    const intl = this.intl;
    return {
      week: {
        columnName: intl.t('week'),
        valuePath: 'week',
        width: 50,
        isReorderable: true,
        isResizable: false,
        textAlign: 'right',
        classNames: 'u-text-mono',
        cellComponent: FormatCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          const isCurrentWeek = moment.utc().isSameOrAfter(moment.utc(model.dueDate), 'week');
          const inFirstLT = moment.utc(model.dueDate).isSameOrBefore(this.executionAlert?.dueDate);
          return {
            value: model.dueDate,
            dataType: 'date',
            format: 'w',
            cellClasses: classNames([isCurrentWeek && 'u-text-bolder', inFirstLT && 'u-color-blue'])
          };
        }
      },
      dueDate: {
        columnName: intl.t('dueDate'),
        sortKey: 'dueDate',
        valuePath: 'dueDate',
        width: 110,
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          const isCurrentDay = moment.utc().isSameOrAfter(moment.utc(model.dueDate), 'days');
          const inFirstLT = moment.utc(model.dueDate).isSameOrBefore(this.executionAlert?.dueDate);
          return {
            value: model.dueDate,
            dataType: 'date',
            cellClasses: classNames([isCurrentDay && 'u-text-bolder', inFirstLT && 'u-color-blue'])
          };
        }
      },
      demandQty: {
        columnName: intl.t('demand'),
        sortKey: 'demandQty',
        valuePath: 'demandQty',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            value: Math.abs(model.demandQty ?? 0),
            dataType: 'number',
            cellClasses: moment.utc().isSameOrAfter(moment.utc(model.dueDate))
              ? 'u-color-success u-text-bold'
              : ''
          };
        }
      },
      supplyQty: {
        columnName: intl.t('supply'),
        sortKey: 'supplyQty',
        valuePath: 'supplyQty',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            value: model.supplyQty,
            dataType: 'number'
          };
        }
      },
      realBalance: {
        columnName: intl.t('realBalance'),
        sortKey: 'realBalance',
        valuePath: 'realBalance',
        width: 75,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            value: model.realBalance ?? 0,
            dataType: 'number',
            cellClasses: classNames([model.realBalance < 0 && 'u-color-red'])
          };
        }
      },
      realBufferPenetration: {
        columnName: intl.t('realBufferPriority'),
        sortKey: 'priority',
        valuePath: 'realBufferPenetration',
        width: 90,
        isReorderable: true,
        isResizable: true,
        cellComponent: PlanningPriorityCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            priorityName: getBufferZoneCriticality(model.realBufferZone),
            bufferPenetration: model.realBufferPenetration
          };
        }
      },
      simulatedDemandQty: {
        columnName: intl.t('simulatedDemand'),
        sortKey: 'simulatedDemandQty',
        valuePath: 'simulatedDemandQty',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            value: Math.abs(model.simulatedDemandQty ?? 0),
            dataType: 'number'
          };
        }
      },
      simulatedBalance: {
        columnName: intl.t('simulatedBalance'),
        sortKey: 'simulatedBalance',
        valuePath: 'simulatedBalance',
        width: 85,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: ({ simulatedBalance }: IProjectedExecutionItemEnriched) => {
          return {
            value: simulatedBalance ?? 0,
            dataType: 'number',
            cellClasses: classNames([simulatedBalance < 0 && 'u-color-red'])
          };
        }
      },
      simulatedBufferPriority: {
        columnName: intl.t('simulatedBufferPriority'),
        sortKey: 'simulatedPriority',
        valuePath: 'simulatedBufferPenetration',
        width: 90,
        isReorderable: true,
        isResizable: true,
        cellComponent: PlanningPriorityCell,
        format: ({
          simulatedBufferZone,
          simulatedBufferPenetration
        }: IProjectedExecutionItemEnriched) => {
          return {
            priorityName: getBufferZoneCriticality(simulatedBufferZone),
            bufferPenetration: simulatedBufferPenetration
          };
        }
      },
      openOrders: {
        columnName: '',
        valuePath: 'openOrders',
        minWidth: 36,
        width: 36,
        isReorderable: true,
        isResizable: true,
        isSortable: false,
        cellComponent: ActionCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            model: { dueDate: model.dueDate },
            context: this,
            visible: true,
            action: (m: IProjectedExecutionItemEnriched) => this.args.showOpenOrders?.(m),
            icon: 'open',
            size: 'sm',
            tooltip: intl.t('showOpenOrders')
          };
        }
      },
      executionTasks: {
        columnName: intl.t('commentsPage.title'),
        valuePath: 'tasks',
        width: 20,
        maxWidth: 70,
        isReorderable: true,
        isResizable: true,
        isSortable: true,
        cellComponent: TaskCell,
        format: (model: IProjectedExecutionItemEnriched) => {
          return {
            model,
            context: this,
            toggleTasks: this.toggleTasks,
            tasks: this.executionTasks,
            isOpened: model.dueDate === this.scrollToDate,
            inPage: !this.args.inModal,
            entityId: this.args.skuId,
            dueDate: model.dueDate,
            taskType: 'EXECUTION_TASK',
            resourceName: this.skuResourceName,
            onDraftTask: this.onDraftTask,
            onSaveTask: this.onSaveTask,
            onDeleteTask: this.onDeleteTask,
            onCloseTasks: this.onCloseTasks
          };
        }
      }
    };
  }

  /**
   * Orders table column definitions
   */
  get ordersTableColumns() {
    const columnsConfig = this.appSettings.features?.execution.projectionColumns ?? [];
    const columns: IBvTableColumn[] = [];
    const { defaultProjectionColumns } = this;
    columnsConfig.forEach((config) => {
      const column = defaultProjectionColumns[config.key as string];
      if (config?.enabled) {
        if (config.title) {
          column.columnName = this.localization.tOrElse(config.title) as string;
        }
        columns.push(column);
      }
    });
    return columns;
  }

  get preferedPoViewId(): string | undefined {
    return this.sessionUser.preferences.getPagePreferences<ISkuPagePrefs>(
      this.router.currentRouteName
    )?.args?.executionPoView;
  }

  get preferedPoView() {
    const id = this.preferedPoViewId;
    return (
      (id && this.collectionBuilderPoGroups.myCollectionViews?.find((view) => view.id === id)) ||
      (this.collectionDefinition.defaultView as CollectionView)
    );
  }

  @cached
  get powViewsMenuItems() {
    const { preferedPoViewId, collectionDefinition, onPoViewsMenuClick } = this;
    const { myCollectionViews = [] } = this.collectionBuilderPoGroups;
    return [
      {
        title: this.intl.t('collectionViews'),
        items: [
          {
            selected: !preferedPoViewId,
            hasArrow: true,
            content: collectionDefinition.defaultView.name,
            onClick: () => onPoViewsMenuClick(collectionDefinition.defaultView as CollectionView)
          },
          ...myCollectionViews.map((view) => {
            return {
              selected: preferedPoViewId === view.id,
              hasArrow: true,
              content: view.name,
              onClick: () => onPoViewsMenuClick(view)
            };
          })
        ]
      }
    ];
  }

  get showPoViewMenu() {
    return this.powViewsMenuItems[0] && this.powViewsMenuItems[0].items.length > 0;
  }

  // Lifecycle Hooks
  // ~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      'plannings',
      'PlanningsCalculationDone',
      this.onPlanningsCalculationDone,
      this
    );
    this.eventStream.addHandler(
      this.skuResourceName.plural,
      TaskListChangeEvents,
      this.handleTaskEvent,
      this
    );
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  // Actions
  // ~~~~~

  @action
  handleDidInsert() {
    this.refreshData.perform();
    this.collectionBuilderPoGroups.fetchPoViews.perform();
  }

  @action
  toggleEmptyRowsVisibility() {
    this.bvMetrics.trackAction(this, 'toggleEmptyRowsVisibility');
    this.emptyRowsVisible = !this.emptyRowsVisible;
  }

  @action
  async handleGeneratePlanning() {
    try {
      const today = moment.utc().format('YYYY-MM-DD');
      const payload = { date: today, skuIds: [this.args.skuId], warehouseIds: [] };
      return await PlanningModel.generateMany(payload);
    } catch (error) {
      defaultErrorHandler(error);
    }
  }

  @action
  toggleTasks(dueDate?: string) {
    this.scrollToDate = dueDate ?? '';
  }

  @action
  onDraftTask(task: SkuTask) {
    this.executionTasks = [...this.executionTasks, task];
  }

  @action
  async onSaveTask(task?: SkuTask) {
    this.executionTasks = this.executionTasks
      .filter((t) => t?.id && t.id !== task?.id)
      .concat(task?.id ? [task] : []);
  }

  @action
  async onDeleteTask(task?: SkuTask) {
    const { skuId } = this.args;

    this.executionTasks = this.executionTasks.filter((t) => t?.id && t.id !== task?.id);
    if (skuId && task?.id) {
      this.alertTask.perform(skuId);
    }
  }

  @action
  async onCloseTasks(task?: SkuTask) {
    if (!task) {
      return;
    }
    if (task.newWithoutComment) {
      await this.onDeleteTask(task);
    } else {
      await this.taskService.saveTask.perform(task);
    }
  }

  @action
  onTaskUpdated(tasks: SkuTask[]) {
    this.executionTasks = tasks;
  }

  // Helper Functions
  // ~~~~~

  onPlanningsCalculationDone(payload: { skuIds: string[] }) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    const skuIds = payload.skuIds || [];
    if (
      skuIds.includes(this.executionAlert?.sku.id as string) ||
      skuIds.includes(this.args.skuId)
    ) {
      this.refreshData.perform();
    }
  }

  async handleTaskEvent(payload: SingleResourceDoc) {
    const { linkedEntityId, taskType } = payload.data.attributes ?? {};
    const skuId = this.executionAlert?.sku.id;
    if (taskType === 'EXECUTION_TASK' && skuId && skuId === linkedEntityId) {
      await this.alertTask.perform(skuId);
    }
  }

  @action
  async onPoViewsMenuClick(view?: CollectionView) {
    const { sku } = this.args;

    if (view) {
      const prefs: IPagesPreferences<ISkuPagePrefs> =
        this.sessionUser.preferences.getPagePreferences<ISkuPagePrefs>(
          this.router.currentRouteName
        ) ?? ({} as IPagesPreferences<ISkuPagePrefs>);

      if (prefs.args?.executionPoView !== view.id) {
        await this.sessionUser.setPageProperty<ISkuPagePrefs>(this.router.currentRouteName, {
          ...prefs,
          args: { executionPoView: view.id }
        });
      }

      const queryParams = collectionViewToQueryParams(
        {
          id: view.id,
          filters: [
            ...(view.filters ?? []),
            { property: 'q', comparator: 'lk', values: [sku.part.code] },
            { property: 'warehouse', comparator: 'in', values: [sku.warehouse.warehouseId] }
          ],
          sortBy: view.sortBy,
          groupBy: view.groupBy,
          pageSize: view.pageSize,
          args: view.args
        },
        {},
        this.collectionDefinition
      );

      this.router.transitionTo('protected.po-workbench', { queryParams });
    }
  }

  refreshData = restartableTask(async () => {
    const { skuId } = this.args;

    if (isPresent(skuId)) {
      this.getChartDataTask.perform(skuId);
      this.alertTask.perform(skuId);
      this.executionTasks = (await this.taskService.fetchExecutionTasks.perform(skuId)) as SkuTask[];
    }
  });

  getChartDataTask = restartableTask(async (skuId: string) => {
    try {
      const payload = (await this.executionAlertsTsData.load.perform(skuId)) as
        | IExecutionAlertTs
        | undefined;
      this.chartData = payload ? { ...payload } : undefined;
      return payload;
    } catch (reason) {
      defaultErrorHandler(reason);
      return;
    }
  });

  alertTask = restartableTask(async (skuId: string) => {
    try {
      const query = {
        filter: {
          skuId: eqFilter(skuId)
        },
        include: 'sku'
      };

      const alerts = (await this.store.query(
        ExecutionResourceName.singular,
        query
      )) as Ember.Array<ExecutionAlertModel>;
      const executionAlert = alerts.firstObject;

      if (!executionAlert) {
        this.notCalculated = true;
        this.projection = undefined;
        return;
      }

      const projection: IProjectedExecutionOrder = await this.store.customQuery(
        'execution-alert',
        'projection',
        executionAlert.id
      );

      this.notCalculated = false;
      this.executionAlert = executionAlert;
      this.projection = projection;

      return executionAlert;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });
}
